// src/components/FileUpload/FileDropZone.js
import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import { useFileUpload } from '../../hooks/useFileUpload';

const FileDropZone = ({ onFileAnalyzed, className, style }) => {
  const {
    isDragging,
    isProcessing,
    handleDragOver,
    handleDragLeave,
    handleDrop
  } = useFileUpload(onFileAnalyzed);

  const [statusMessage, setStatusMessage] = useState('AI analyzing your file...');
  const [uploadedImage, setUploadedImage] = useState(null);

  useEffect(() => {
    if (!isProcessing) {
      setUploadedImage(null); // Reset image when not processing
      return;
    }

    // Rotate through status messages
    const messages = [
      "Ryder is examining visual elements...",
      "Analyzing document structure...",
      "Identifying key features...",
      "Processing business context...",
      "Aetheris is reviewing findings...",
      "Generating comprehensive insights..."
    ];

    let messageIndex = 0;
    const interval = setInterval(() => {
      setStatusMessage(messages[messageIndex % messages.length]);
      messageIndex++;
    }, 4000);

    return () => clearInterval(interval);
  }, [isProcessing]);

  const handleFileDropWithPreview = async (e) => {
    e.preventDefault();

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    
    // Check if it's an image and create preview
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (imageTypes.includes(file.type)) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }

    // Call the original handleDrop function
    handleDrop(e);
  };

  return (
    <div
      style={{
        border: isDragging ? '2px solid #3b82f6' : '2px dashed rgba(59, 130, 246, 0.3)',
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center',
        background: isDragging ? 'rgba(59, 130, 246, 0.1)' : 'rgba(15, 23, 42, 0.6)',
        transition: 'all 0.3s ease',
        margin: '16px 0',
        position: 'relative',
        overflow: 'hidden',
        ...style
      }}
      className={className}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleFileDropWithPreview}
    >
      {/* Breathing Image Background */}
      {isProcessing && uploadedImage && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${uploadedImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.3,
            animation: 'imageBreathing 3s ease-in-out infinite',
            filter: 'blur(2px)',
            zIndex: 0
          }}
        />
      )}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {isProcessing ? (
          <div>
            {uploadedImage && (
              <div style={{
                width: '200px',
                height: '200px',
                margin: '0 auto 20px',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                animation: 'imagePulse 3s ease-in-out infinite'
              }}>
                <img 
                  src={uploadedImage} 
                  alt="Processing"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            )}
            
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <Loader size={24} className="animate-spin" style={{ color: '#3b82f6' }} />
              <div style={{
                display: 'flex',
                gap: '4px'
              }}>
                <span className="processing-dot" style={{ animationDelay: '0s' }}>•</span>
                <span className="processing-dot" style={{ animationDelay: '0.2s' }}>•</span>
                <span className="processing-dot" style={{ animationDelay: '0.4s' }}>•</span>
              </div>
            </div>
            
            <p style={{ 
              color: '#e2e8f0', 
              fontWeight: '500',
              fontSize: '14px',
              marginBottom: '4px'
            }}>
              {statusMessage}
            </p>
            <p style={{ 
              color: '#94a3b8', 
              fontSize: '12px',
              animation: 'fadeInOut 3s ease-in-out infinite'
            }}>
              This may take 30-45 seconds for comprehensive analysis
            </p>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📄</div>
            <h4 style={{ color: '#e2e8f0', marginBottom: '8px' }}>
              Drop your file here for AI analysis
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '12px' }}>
              Supports images, CSV, Excel, JSON, TXT files
            </p>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes imageBreathing {
          0%, 100% { 
            opacity: 0.2;
            transform: scale(1);
          }
          50% { 
            opacity: 0.4;
            transform: scale(1.05);
          }
        }

        @keyframes imagePulse {
          0%, 100% { 
            opacity: 1;
            transform: scale(1);
          }
          50% { 
            opacity: 0.4;
            transform: scale(0.98);
          }
        }

        @keyframes fadeInOut {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        .processing-dot {
          color: #3b82f6;
          font-size: 20px;
          animation: dotPulse 1.5s ease-in-out infinite;
        }

        @keyframes dotPulse {
          0%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default FileDropZone;
